import {
    faEquals,
    faLayerGroup,
    faPencilAlt,
    faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Badge, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { formatQuantityToLocale } from "../../../../../utils/method";
import "./ExpandablePositionsTable.scss";

const Header = ({
    group,
    position,
    handleEditGroup,
    handleDeleteGroup,
    readonly,
    isCarriedForward,
    renderCarriedForwardBadge = () => { },
}) => {
    const { t } = useTranslation();

    const { groupId, groupName, lines: groupLines } = group;

    const { readonly: storeReadonly, positionUnitMap } = useSelector((state) => state.aufmassV2);

    // Use prop readonly or store readonly
    const isReadonly = readonly !== undefined ? readonly : storeReadonly;

    // Use the passed isCarriedForward prop or check if lines are carried forward
    const isGroupCarriedForward = isCarriedForward || (groupLines?.length > 0 && groupLines?.[0].isCarriedForward);

    return (
        <div className="aufmass-line-group-header">
            <div className="d-flex justify-content-between align-items-center w-100">
                <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                    <span className="group-name">
                        {groupName}
                        {renderCarriedForwardBadge && renderCarriedForwardBadge(groupLines[0])}
                    </span>
                </div>
                <div className="d-flex align-items-center">
                    {/* Group subtotal displayed in header */}
                    <div className="me-3">
                        <span className="me-2 text-muted">
                            <FontAwesomeIcon
                                icon={faEquals}
                                className="me-1"
                                size="sm"
                            />
                            {t("aufmass.groups.subtotal")}:
                        </span>
                        <Badge
                            bg="secondary"
                            className="py-1 px-2 am-badge-sm"
                        >
                            {formatQuantityToLocale(group.totalQuantity)}{" "}
                            {positionUnitMap[position.id] || position.unit}
                        </Badge>
                    </div>
                    {/* Only show actions if handlers are provided and not in readonly mode */}
                    {!isReadonly && handleEditGroup && handleDeleteGroup && (
                        <div className="group-actions">
                            <Button
                                variant="link"
                                size="sm"
                                className="text-primary p-0 me-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (handleEditGroup) {
                                        handleEditGroup(
                                            groupId,
                                            groupName,
                                            groupLines,
                                        );
                                    }
                                }}
                                title={isGroupCarriedForward ? t("aufmass.cannot-edit-carried-forward") : t("aufmass.groups.edit")}
                                disabled={isReadonly || isGroupCarriedForward}
                            >
                                <FontAwesomeIcon icon={faPencilAlt} />
                            </Button>
                            <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (handleDeleteGroup) {
                                        handleDeleteGroup(groupId, position.id);
                                    }
                                }}
                                title={isGroupCarriedForward ? t("aufmass.cannot-delete-carried-forward") : t("aufmass.groups.delete")}
                                disabled={isReadonly || isGroupCarriedForward}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// New component for the entire group wrapper with React.memo to prevent unnecessary re-renders
const Wrapper = React.memo(({ children }) => {
    return <div className="aufmass-group-wrapper">{children}</div>;
});

const GroupItemV2 = {
    Header,
    Wrapper,
};

export default GroupItemV2;
